import { FormEvent, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Brand } from '../components/Brand';
import { downloadSignedAgreement, createFeature, deleteMedia, deleteMediaSelectDelivery, getAdminActivity, listFeatures, updateFeature, uploadMedia, uploadMediaSelectDelivery } from '../lib/api';
import type { AdminActivity, FeatureDraft, FeaturePortal, MediaItem, PlatformLink } from '../types';
import { demoMode, supabase } from '../lib/supabase';
import { clearFinishedUploadJobs, enqueueFeatureUploads, getUploadSnapshot, retryUploadJob, subscribeToUploads } from '../lib/uploadQueue';

const destinationPresets: PlatformLink[] = [
  { platform: 'ROAM', url: 'https://roamgroup.media', label: 'Read on ROAM', enabled: true },
  { platform: 'YouTube', url: 'https://www.youtube.com/@ROAMGROUP', label: 'Watch on YouTube', enabled: true },
  { platform: 'Instagram', url: 'https://www.instagram.com/roamgroup/', label: 'View on Instagram', enabled: true },
  { platform: 'TikTok', url: 'https://www.tiktok.com/@devinsellsmiami', label: 'View on TikTok', enabled: true },
];

const defaultThankYou = 'Thank you for trusting ROAM to document and share your story. Our goal is to keep these editorial features completely free for the people we feature. Your willingness to let us into your world is what makes that possible.';

function freshDraft(): FeatureDraft {
  return {
    title: '',
    series: '',
    participantName: '',
    participantEmail: '',
    slug: '',
    thankYou: defaultThankYou,
    storyTitle: '',
    storyBody: '',
    publicStoryUrl: '',
    requiresPublicationApproval: true,
    requiresFeatureRelease: true,
    links: destinationPresets.map((link) => ({ ...link, url: '', enabled: false })),
  };
}

function slugify(value: string) {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function paymentLabel(status?: string, totalCents = 0) {
  if (status === 'paid') return 'PAID';
  if (status === 'pending') return 'PAYMENT PENDING';
  if (status === 'expired') return 'PAYMENT EXPIRED';
  if (status === 'failed') return 'PAYMENT FAILED';
  if (status === 'not_collected') return 'NOT COLLECTED';
  if (status === 'not_required' || totalCents === 0) return 'NO PAYMENT REQUIRED';
  return status ? status.replace(/_/g, ' ').toUpperCase() : 'PAYMENT STATUS UNKNOWN';
}

interface SelectUploadDraft {
  file: File;
  title: string;
  description: string;
  freeWatermarked: boolean;
  cleanPrice: number;
  preparedPrice: number;
}

function UploadQueuePanel() {
  const jobs = useSyncExternalStore(subscribeToUploads, getUploadSnapshot);
  if (!jobs.length) return null;
  const pending = jobs.filter((job) => job.state === 'queued' || job.state === 'uploading').length;
  const failures = jobs.filter((job) => job.state === 'failed').length;
  return <section className="upload-queue" aria-label="ROAM background media uploads">
    <div className="upload-queue__heading"><div><strong>Media uploads</strong><small>{pending ? `${pending} in progress or queued · keep this tab open` : failures ? `${failures} need attention` : 'All uploads finished'}</small></div><button type="button" className="text-link text-link--button" onClick={clearFinishedUploadJobs} disabled={pending > 0}>Clear finished</button></div>
    <div className="upload-queue__jobs">
      {jobs.map((job) => <div key={job.id} className="upload-queue__job">
        <div className="upload-queue__job-title"><strong title={job.fileName}>{job.fileName}</strong><span>{job.progress}%</span></div>
        <small>{job.featureName} · {(job.bytes / 1024 / 1024).toFixed(1)} MB</small>
        <progress value={job.progress} max={100} aria-label={`${job.fileName} upload progress`} />
        <div className="upload-queue__status"><span>{job.state === 'failed' ? `Error: ${job.message}` : job.message}</span>{job.state === 'failed' && <button type="button" className="text-link text-link--button" onClick={() => retryUploadJob(job.id)}>Retry file</button>}</div>
      </div>)}
    </div>
    {pending > 0 && <p>Uploads continue while you edit other ROAM features. Do not refresh, close this tab, or shut down your computer until all uploads finish.</p>}
  </section>;
}

export function Admin() {
  const navigate = useNavigate();
  const [features, setFeatures] = useState<FeaturePortal[]>([]);
  const [activity, setActivity] = useState<AdminActivity>({ mediaSelectOrders: [], commercialInquiries: [], referrals: [], reviewCorrections: [] });
  const [draft, setDraft] = useState<FeatureDraft>(freshDraft());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [saved, setSaved] = useState('');
  const [queuedNotice, setQueuedNotice] = useState('');
  const [featureFiles, setFeatureFiles] = useState<File[]>([]);
  const [selectFiles, setSelectFiles] = useState<SelectUploadDraft[]>([]);
  const [existingMedia, setExistingMedia] = useState<MediaItem[]>([]);
  const [removingMediaId, setRemovingMediaId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deliveryBusy, setDeliveryBusy] = useState<string | null>(null);
  const [accessStatus, setAccessStatus] = useState<'checking' | 'granted' | 'denied' | 'error'>('checking');
  const [signedInEmail, setSignedInEmail] = useState('');
  const uploadJobs = useSyncExternalStore(subscribeToUploads, getUploadSnapshot);
  const completedJobIds = uploadJobs.filter((job) => job.state === 'complete').map((job) => job.id).join(',');

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        if (!demoMode) {
          if (!supabase) throw new Error('Secure sign-in is not configured.');
          const { data, error: sessionError } = await supabase.auth.getSession();
          if (sessionError) throw sessionError;
          if (!data.session) {
            navigate(`/login?next=${encodeURIComponent('/admin')}`, { replace: true });
            return;
          }
          if (active) setSignedInEmail(data.session.user.email ?? '');
        }

        // The Worker authorizes BOTH endpoints. Never render admin actions
        // until the server has confirmed access to both datasets.
        const [featureData, activityData] = await Promise.all([listFeatures(), getAdminActivity()]);
        if (!active) return;
        setFeatures(featureData);
        setActivity(activityData);
        setAccessStatus('granted');
      } catch (error) {
        if (!active) return;
        const message = error instanceof Error ? error.message : 'Unable to verify administrator access.';
        setAccessStatus(/admin access required|forbidden/i.test(message) ? 'denied' : 'error');
      }
    }

    void load();
    return () => { active = false; };
  }, [navigate]);

  // A queued file is not yet part of the returned feature until R2 confirms it.
  // Refresh the dashboard when background transfers complete, including after the editor closes.
  useEffect(() => {
    if (accessStatus !== 'granted' || !completedJobIds) return;
    let active = true;
    void listFeatures().then((data) => { if (active) setFeatures(data); }).catch(() => undefined);
    return () => { active = false; };
  }, [completedJobIds, accessStatus]);

  function patchLink(index: number, changes: Partial<PlatformLink>) {
    const links = [...draft.links];
    links[index] = { ...links[index], ...changes };
    setDraft({ ...draft, links });
  }

  function openCreate() {
    setEditingId(null);
    setDraft(freshDraft());
    setFeatureFiles([]);
    setSelectFiles([]);
    setExistingMedia([]);
    setFormError('');
    setShowCreate(true);
  }

  function openEdit(feature: FeaturePortal) {
    setEditingId(feature.id);
    const byPlatform = new Map(feature.links.map((link) => [link.platform, link]));
    const links = destinationPresets.map((preset) => {
      const existing = byPlatform.get(preset.platform);
      return existing ? { ...existing, enabled: true } : { ...preset, enabled: false };
    });
    for (const link of feature.links) {
      if (!links.some((candidate) => candidate.platform === link.platform)) links.push({ ...link, enabled: true });
    }
    setDraft({
      title: feature.title,
      series: feature.series,
      participantName: feature.participantName,
      participantEmail: feature.participantEmail,
      slug: feature.slug,
      thankYou: feature.thankYou,
      storyTitle: feature.storyTitle,
      storyBody: feature.storyBody,
      publicStoryUrl: feature.publicStoryUrl || '',
      requiresPublicationApproval: feature.publicationApproval.required,
      requiresFeatureRelease: feature.requiresFeatureRelease,
      links,
    });
    setFeatureFiles([]);
    setSelectFiles([]);
    setExistingMedia(feature.media ?? []);
    setFormError('');
    setShowCreate(true);
  }

  async function removeExistingMedia(item: MediaItem) {
    const confirmed = window.confirm(`Remove “${item.title}” from this participant portal? This also deletes the stored media file.`);
    if (!confirmed) return;
    setRemovingMediaId(item.id);
    setFormError('');
    try {
      await deleteMedia(item.id);
      setExistingMedia((current) => current.filter((media) => media.id !== item.id));
      const featureData = await listFeatures();
      setFeatures(featureData);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Unable to remove media.');
    } finally {
      setRemovingMediaId(null);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      if (selectFiles.some((item) => !item.title.trim())) {
        throw new Error('Please give each new Media Select a descriptive display title before saving.');
      }
      if (draft.links.some((link) => link.enabled !== false && !/^https?:\/\/\S+/i.test(link.url.trim()))) {
        throw new Error('Enter the direct published URL for each checked destination, or uncheck the destination.');
      }
      const payload = { ...draft, series: draft.series.trim(), links: draft.links.filter((link) => link.enabled !== false && link.url.trim()) };
      const feature = editingId ? await updateFeature(editingId, payload) : await createFeature(payload);

      // Saving the editorial record must not block on multi-gigabyte media transfers.
      // The shared queue retains each File while this browser tab is open.
      const uploads = [
        ...featureFiles.map((file) => ({ file, options: { role: 'feature' as const, downloadable: false } })),
        ...selectFiles.map((item) => ({ file: item.file, options: {
          role: 'select' as const,
          title: item.title,
          description: item.description,
          downloadable: false,
          freeWatermarked: item.freeWatermarked,
          cleanPriceCents: Math.round(item.cleanPrice * 100),
          preparedPriceCents: Math.round(item.preparedPrice * 100),
        } })),
      ];
      if (uploads.length) enqueueFeatureUploads(feature.id, feature.title, uploads);

      const [featureData, activityData] = await Promise.all([listFeatures(), getAdminActivity()]);
      setFeatures(featureData);
      setActivity(activityData);
      setSaved(feature.slug);
      setQueuedNotice(uploads.length ? `${uploads.length} media upload${uploads.length === 1 ? '' : 's'} queued. You can continue editing other features while uploads run.` : '');
      setShowCreate(false);
      setEditingId(null);
      setDraft(freshDraft());
      setFeatureFiles([]);
      setSelectFiles([]);
      setExistingMedia([]);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Unable to save feature.');
    } finally {
      setSaving(false);
    }
  }

  async function uploadDelivery(requestId: string, file?: File) {
    if (!file) return;
    setDeliveryBusy(requestId);
    setFormError('');
    try {
      await uploadMediaSelectDelivery(requestId, file);
      setActivity(await getAdminActivity());
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Unable to upload Media Select delivery.');
    } finally { setDeliveryBusy(null); }
  }

  async function removeDelivery(deliveryId: string, requestId: string) {
    if (!window.confirm('Remove this delivery file? The participant will no longer be able to download it.')) return;
    setDeliveryBusy(requestId);
    setFormError('');
    try {
      await deleteMediaSelectDelivery(deliveryId);
      setActivity(await getAdminActivity());
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Unable to remove delivery file.');
    } finally { setDeliveryBusy(null); }
  }

  async function signOut() {
    if (getUploadSnapshot().some((job) => job.state === 'queued' || job.state === 'uploading')) {
      window.alert('Media uploads are still running. Please wait for them to finish before signing out.');
      return;
    }
    await supabase?.auth.signOut();
    navigate('/login?next=%2Fadmin', { replace: true });
  }

  const activeLinks = useMemo(() => draft.links.filter((link) => link.enabled !== false), [draft.links]);
  const existingSeries = useMemo(() => [...new Set(features.map((feature) => feature.series.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [features]);

  if (accessStatus !== 'granted') {
    const checking = accessStatus === 'checking';
    const denied = accessStatus === 'denied';
    return (
      <main className="gate-page">
        <nav className="topbar"><Brand small /><Link to="/">ROAM</Link></nav>
        <section className="login-card" role={checking ? 'status' : 'alert'}>
          <div className="eyebrow">ROAM ADMINISTRATION</div>
          <h1>{checking ? 'Checking access.' : denied ? 'Administrator access required.' : 'Unable to verify access.'}</h1>
          <p>{checking ? 'Verifying your administrator permissions.' : denied ? 'This account is not authorized to manage ROAM features. You can still access a participant feature assigned to your email through its direct portal link.' : 'We could not verify administrator permissions. Please try again.'}</p>
          {!checking && (
            <div className="button-row">
              <Link className="button button--light" to="/">Return to ROAM</Link>
              {!denied && <button type="button" className="button button--ghost" onClick={() => window.location.reload()}>Try again</button>}
            </div>
          )}
        </section>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <aside className="admin-nav">
        <Brand />
        <div><span className="eyebrow">MEDIA ADMIN</span><p>Participant portals</p></div>
        <nav><a href="#features">Features</a><a href="#requests">Media requests</a><a href="#commercial">Commercial</a><a href="#corrections">Review notes</a><a href="#agreements">Agreements</a><a href="#referrals">Referrals</a></nav>
        <div className="admin-account"><small>Admin: {signedInEmail || 'ROAM administrator'}</small><button className="text-link text-link--button" onClick={signOut}>Sign out</button></div>
      </aside>
      <section className="admin-main" id="features">
        <UploadQueuePanel />
        <header className="admin-header">
          <div><div className="eyebrow">ROAM FEATURES</div><h1>Build once. Publish endlessly.</h1></div>
          <button className="button button--dark" onClick={openCreate}>+ Create feature</button>
        </header>

        {formError && !showCreate && <div className="notice notice--error">{formError}</div>}
        {saved && <div className="notice">Feature saved. {queuedNotice && <span>{queuedNotice} </span>}<Link to={`/portal/${saved}`}>Open participant portal ↗</Link></div>}

        <div className="feature-table">
          <div className="feature-row feature-row--head"><span>Feature</span><span>Participant</span><span>Release</span><span>Review</span><span>Status</span><span></span></div>
          {features.map((f) => (
            <div className="feature-row" key={f.id}>
              <div><strong>{f.title}</strong><small>{f.series}</small></div>
              <span>{f.participantName}</span>
              <span>{!f.requiresFeatureRelease ? 'Not required' : f.legal.featureRelease.acceptedAt ? 'Accepted' : 'Pending'}</span>
              <span>{f.legal.privateReview.acceptedAt ? 'Accepted' : 'Pending'}</span>
              <span className="status-dot">{f.status}</span>
              <div className="row-actions"><button className="text-link text-link--button" onClick={() => openEdit(f)}>Edit</button>{f.requiresFeatureRelease && <Link to={`/release/${f.slug}`}>Release ↗</Link>}<Link to={`/portal/${f.slug}`}>Portal ↗</Link>{f.legal.featureRelease.acceptedAt && <button type="button" className="text-link text-link--button" onClick={() => { void downloadSignedAgreement(f.slug, 'feature_release').catch((e) => setFormError(e instanceof Error ? e.message : 'Signed release unavailable.')); }}>Signed 01 ↓</button>}{f.legal.privateReview.acceptedAt && <button type="button" className="text-link text-link--button" onClick={() => { void downloadSignedAgreement(f.slug, 'private_review').catch((e) => setFormError(e instanceof Error ? e.message : 'Signed terms unavailable.')); }}>Signed 02 ↓</button>}</div>
            </div>
          ))}
        </div>

        <section className="admin-info" id="requests"><div className="eyebrow">MEDIA SELECT CHECKOUTS</div><h2>One request. One license. Every selected asset.</h2>{activity.mediaSelectOrders.length === 0 ? <p>No Media Select requests yet.</p> : <div className="activity-list">{activity.mediaSelectOrders.map((order) => <article className="activity-card activity-card--order" key={order.id}>
          <div><strong>{order.legacy ? 'Legacy Media Select request' : `Request ${order.id.slice(0, 8).toUpperCase()}`}</strong><span>{order.participantName} · {order.featureTitle}</span><span className={`payment-badge payment-badge--${order.paymentStatus || 'unknown'}`}>{paymentLabel(order.paymentStatus, order.totalCents)}</span></div>
          <div><span>{order.items.length} item{order.items.length === 1 ? '' : 's'}</span><strong>${(order.totalCents / 100).toFixed(2)}</strong></div>
          <div className="order-items">{order.items.map((item) => <div className="order-item order-item--delivery" key={item.id}><div><span>{item.mediaTitle} · {item.tier === 'share' ? 'ROAM Share' : item.tier === 'clean' ? 'Clean Select' : 'Prepared by ROAM'}</span><strong>${(item.basePriceCents / 100).toFixed(2)}</strong></div>{!order.legacy && <div className="delivery-admin">{item.delivery ? <><span className={`delivery-pill delivery-pill--${item.delivery.status}`}>{item.delivery.status === 'ready' ? 'DELIVERY READY' : 'UPLOADING'}</span><small>{item.delivery.originalName}</small><button type="button" className="text-link text-link--button" disabled={deliveryBusy === item.requestId} onClick={() => removeDelivery(item.delivery!.id, item.requestId)}>{deliveryBusy === item.requestId ? 'Working…' : 'Remove delivery'}</button></> : <label className="delivery-upload"><span>{item.basePriceCents > 0 && order.paymentStatus !== 'paid' ? 'Upload deliverable — locked until payment' : 'Upload deliverable'}</span><input type="file" accept="video/*,image/*,.pdf,.zip" disabled={deliveryBusy === item.requestId} onChange={(e) => { const file = e.target.files?.[0]; void uploadDelivery(item.requestId, file); e.currentTarget.value = ''; }} /></label>}</div>}</div>)}</div>
          <div className="order-breakdown"><span>Media <strong>${(order.subtotalCents / 100).toFixed(2)}</strong></span><span>Optional support <strong>${(order.supportCents / 100).toFixed(2)}</strong></span></div>
          {order.note && <p>{order.note}</p>}
          <small>{order.legacy ? 'Legacy per-select acceptance' : `${order.licenseVersion || 'License'} · accepted by ${order.typedName || order.email}`} · {new Date(order.licenseAcceptedAt || order.createdAt).toLocaleString()} · {order.status}{order.paidAt ? ` · paid ${new Date(order.paidAt).toLocaleString()}` : ''}</small>
        </article>)}</div>}</section>
        <section className="admin-info" id="commercial"><div className="eyebrow">COMMISSION ROAM</div><h2>Commercial inquiries stay distinct from editorial.</h2>{activity.commercialInquiries.length === 0 ? <p>No commercial inquiries yet.</p> : <div className="activity-list">{activity.commercialInquiries.map((inquiry) => <article className="activity-card" key={inquiry.id}><div><strong>{inquiry.projectType}</strong><span>{inquiry.participantName} · {inquiry.email}</span></div><p>{inquiry.details}</p><small>{inquiry.timeline || 'Timing not specified'} · {inquiry.budgetRange || 'Budget not specified'} · {inquiry.status}</small></article>)}</div>}</section>
        <section className="admin-info" id="corrections"><div className="eyebrow">PARTICIPANT FACTUAL CORRECTIONS</div><h2>Review notes from the people we feature.</h2>{activity.reviewCorrections.length === 0 ? <p>No factual corrections submitted.</p> : <div className="activity-list">{activity.reviewCorrections.map((entry) => <article className="activity-card" key={entry.id}><div><strong>{entry.featureTitle}</strong><span>{entry.participantName} · {entry.email}</span></div><p>{entry.correction}</p><small>{new Date(entry.createdAt).toLocaleString()}</small></article>)}</div>}</section>
        <section className="admin-info" id="agreements"><div className="eyebrow">LEGAL WORKFLOW</div><h2>Two required agreements, plus event-based records.</h2><p><strong>Before filming:</strong> ROAM Participant Release (01). <strong>Before private page access:</strong> Private Page Access Terms (02). <strong>Courtesy review:</strong> a participant may confirm or request a factual correction; this is not an approval right. <strong>At Media Select checkout:</strong> Media License Agreement (03) and an exact License Record for the transaction.</p><p>Each acceptance stores the active document version and snapshot instead of relying on a single boolean flag.</p></section>
        <section className="admin-info" id="referrals"><div className="eyebrow">REFERRALS</div><h2>Future stories arrive through the portal.</h2>{activity.referrals.length === 0 ? <p>No referrals yet.</p> : <div className="activity-list">{activity.referrals.map((referral) => <article className="activity-card" key={referral.id}><div><strong>{referral.name}</strong><span>Referred by {referral.participantName}</span></div>{referral.note && <p>{referral.note}</p>}<small>{referral.email || 'No email provided'} · {new Date(referral.createdAt).toLocaleString()}</small></article>)}</div>}</section>
      </section>

      {showCreate && (
        <div className="modal-backdrop" onMouseDown={() => setShowCreate(false)}>
          <form className="feature-builder" onSubmit={submit} onMouseDown={(e) => e.stopPropagation()}>
            <div className="builder-header"><div><div className="eyebrow">{editingId ? 'EDIT FEATURE' : 'NEW FEATURE'}</div><h2>{editingId ? 'Update participant portal' : 'Create a participant portal'}</h2></div><button type="button" className="icon-button" onClick={() => setShowCreate(false)}>×</button></div>

            {formError && <div className="notice notice--error">{formError}</div>}

            <div className="form-grid">
              <label>Feature title<input required value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value, slug: draft.slug || slugify(e.target.value) })} placeholder="Feature title" /></label>
              <label>Series
                <input required list="roam-series" value={draft.series} onChange={(e) => setDraft({ ...draft, series: e.target.value })} placeholder="Choose a series or enter a new one" autoComplete="off" />
                <datalist id="roam-series">{existingSeries.map((series) => <option key={series} value={series} />)}</datalist>
              </label>
              <label>Participant name<input required value={draft.participantName} onChange={(e) => setDraft({ ...draft, participantName: e.target.value })} placeholder="Participant name" /></label>
              <label>Participant email<input required type="email" value={draft.participantEmail} onChange={(e) => setDraft({ ...draft, participantEmail: e.target.value })} placeholder="participant@example.com" /></label>
              <label className="span-2">Portal slug<input required value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: slugify(e.target.value) })} placeholder="participant-name" /></label>
              <label className="span-2">Thank-you message<textarea required rows={4} value={draft.thankYou} onChange={(e) => setDraft({ ...draft, thankYou: e.target.value })} /></label>
              <label className="span-2">Story headline<input value={draft.storyTitle} onChange={(e) => setDraft({ ...draft, storyTitle: e.target.value })} placeholder="Story headline" /></label>
              <label className="span-2">Written story<textarea rows={9} value={draft.storyBody} onChange={(e) => setDraft({ ...draft, storyBody: e.target.value })} placeholder="Paste the finished editorial story here…" /></label>
              <label className="span-2">Public ROAM story URL<input value={draft.publicStoryUrl} onChange={(e) => setDraft({ ...draft, publicStoryUrl: e.target.value })} placeholder="https://roamgroup.media/stories/..." /></label>
            </div>

            <div className="builder-subsection">
              <h3>Free feature media</h3>
              <p className="builder-help">The finished feature stays prominent. Upload the feature video, photos, or supporting documents here.</p>
              {editingId && existingMedia.some((item) => item.role !== 'select') && <div className="existing-media-list">
                <div className="eyebrow">CURRENT FEATURE MEDIA</div>
                {existingMedia.filter((item) => item.role !== 'select').map((item) => <div className="existing-media-row" key={item.id}><div><strong>{item.title}</strong><small>{item.kind.toUpperCase()}{item.bytes ? ` · ${(item.bytes / 1024 / 1024).toFixed(1)} MB` : ''}</small></div><button type="button" className="text-link text-link--button existing-media-remove" onClick={() => removeExistingMedia(item)} disabled={removingMediaId === item.id}>{removingMediaId === item.id ? 'Removing…' : 'Remove'}</button></div>)}
              </div>}
              <label className="upload-drop"><strong>Choose feature media</strong><span>Video and image assets render according to their actual media type.</span><input type="file" multiple accept="video/*,image/*,.pdf,.doc,.docx" onChange={(e) => setFeatureFiles(Array.from(e.target.files ?? []))} /></label>
              {featureFiles.length > 0 && <div className="upload-list">{featureFiles.map((file) => <div key={`${file.name}-${file.size}`}><span>{file.name}</span><small>{(file.size / 1024 / 1024).toFixed(1)} MB</small></div>)}</div>}
            </div>

            <div className="builder-subsection">
              <h3>ROAM Media Selects</h3>
              <p className="builder-help">Upload preview-only versions here (watermarked or reduced-resolution when appropriate). Final licensed deliverables are uploaded separately to the paid/free order after checkout. Paid selections use one limited-use license acceptance and Stripe-hosted checkout; complimentary selections skip payment.</p>
              {editingId && existingMedia.some((item) => item.role === 'select') && <div className="existing-media-list existing-media-list--selects">
                <div className="eyebrow">CURRENT MEDIA SELECTS · {existingMedia.filter((item) => item.role === 'select').length}</div>
                {existingMedia.filter((item) => item.role === 'select').map((item) => <div className="existing-media-row existing-media-row--select" key={item.id}><div><strong>{item.title}</strong><small>{item.kind.toUpperCase()}{item.cleanPriceCents != null ? ` · Clean $${(item.cleanPriceCents / 100).toFixed(item.cleanPriceCents % 100 ? 2 : 0)}` : ''}{item.preparedPriceCents != null ? ` · Prepared by ROAM $${(item.preparedPriceCents / 100).toFixed(item.preparedPriceCents % 100 ? 2 : 0)}` : ''}{item.freeWatermarked ? ' · ROAM Share enabled' : ''}</small>{item.description && <span>{item.description}</span>}</div><button type="button" className="text-link text-link--button existing-media-remove" onClick={() => removeExistingMedia(item)} disabled={removingMediaId === item.id}>{removingMediaId === item.id ? 'Removing…' : 'Remove'}</button></div>)}
              </div>}
              <label className="upload-drop"><strong>Add select preview footage or photos</strong><span>Use preview-safe files here. Each select gets a complimentary ROAM Share option plus editable Clean Select and Prepared by ROAM prices.</span><input type="file" multiple accept="video/*,image/*" onChange={(e) => setSelectFiles(Array.from(e.target.files ?? []).map((file) => ({ file, title: '', description: '', freeWatermarked: true, cleanPrice: 25, preparedPrice: 100 })))} /></label>
              <div className="select-editor-list">
                {selectFiles.map((item, index) => (
                  <div className="select-editor" key={`${item.file.name}-${item.file.size}`}>
                    <div className="select-editor__heading"><strong>{item.file.name}</strong><small>{(item.file.size / 1024 / 1024).toFixed(1)} MB</small></div>
                    <label>Display title (required)<input required placeholder="e.g. Sunrise at the Marina" value={item.title} onChange={(e) => setSelectFiles(selectFiles.map((x, i) => i === index ? { ...x, title: e.target.value } : x))} /></label>
                    <label>Description<input value={item.description} onChange={(e) => setSelectFiles(selectFiles.map((x, i) => i === index ? { ...x, description: e.target.value } : x))} placeholder="Why this shot is worth offering" /></label>
                    <div className="price-grid"><label>Clean select ($)<input type="number" min="0" value={item.cleanPrice} onChange={(e) => setSelectFiles(selectFiles.map((x, i) => i === index ? { ...x, cleanPrice: Number(e.target.value) } : x))} /></label><label>Prepared by ROAM ($)<input type="number" min="0" value={item.preparedPrice} onChange={(e) => setSelectFiles(selectFiles.map((x, i) => i === index ? { ...x, preparedPrice: Number(e.target.value) } : x))} /></label></div>
                    <label className="inline-check"><input type="checkbox" checked={item.freeWatermarked} onChange={(e) => setSelectFiles(selectFiles.map((x, i) => i === index ? { ...x, freeWatermarked: e.target.checked } : x))} /><span>Offer a complimentary ROAM-watermarked version</span></label>
                  </div>
                ))}
              </div>
            </div>

            <div className="builder-subsection">
              <div className="subsection-heading"><div><h3>Publishing destinations</h3><p className="builder-help">New features start with no destinations selected. Check a destination only after this feature is published there, and use the direct URL to that feature rather than a generic profile or homepage.</p></div><span className="destination-count">{activeLinks.length} selected</span></div>
              <div className="destination-list">
                {draft.links.map((link, i) => (
                  <div className="destination-row" key={`${link.platform}-${i}`}>
                    <input aria-label={`Use ${link.platform}`} type="checkbox" checked={link.enabled !== false} onChange={(e) => patchLink(i, { enabled: e.target.checked })} />
                    <select value={link.platform} onChange={(e) => patchLink(i, { platform: e.target.value as PlatformLink['platform'] })}><option>ROAM</option><option>YouTube</option><option>Instagram</option><option>TikTok</option><option>Facebook</option><option>Other</option></select>
                    <input value={link.url} onChange={(e) => patchLink(i, { url: e.target.value })} placeholder="Destination URL" />
                    <input value={link.label || ''} onChange={(e) => patchLink(i, { label: e.target.value })} placeholder="Button label" />
                  </div>
                ))}
              </div>
              <button type="button" className="text-link text-link--button" onClick={() => setDraft({ ...draft, links: [...draft.links, { platform: 'Other', url: '', label: 'View feature', enabled: false }] })}>+ Add destination</button>
            </div>

            <div className="builder-subsection legal-workflow-panel"><h3>Legal workflow</h3><p className="builder-help">The release is required before filming. Private Page Terms are accepted on first access. Publication review is a courtesy, not an approval condition. Media Select licensing happens at checkout.</p><label className="consent-row consent-row--light"><input type="checkbox" checked={draft.requiresFeatureRelease} onChange={(e) => setDraft({ ...draft, requiresFeatureRelease: e.target.checked })} /><span>Require ROAM Participant Release (01) before filming.</span></label>{editingId && draft.requiresFeatureRelease && <p className="builder-help">Pre-shoot release link: <a href={`/release/${draft.slug}`} target="_blank" rel="noreferrer">Open participant release ↗</a></p>}</div>

            <label className="consent-row consent-row--light"><input type="checkbox" checked={draft.requiresPublicationApproval} onChange={(e) => setDraft({ ...draft, requiresPublicationApproval: e.target.checked })} /><span>Show the optional participant publication review and factual-correction step.</span></label>
            <div className="builder-footer"><button type="button" className="button button--ghost-dark" onClick={() => setShowCreate(false)}>Cancel</button><button className="button button--dark" disabled={saving}>{saving ? 'Saving feature…' : (featureFiles.length || selectFiles.length) ? 'Save & queue uploads' : editingId ? 'Save changes' : 'Create portal'}</button></div>
          </form>
        </div>
      )}
    </main>
  );
}
